import { SxProps, Theme } from '@mui/material';
import { differenceInMonths, differenceInYears } from 'date-fns';

import { ETypeVaccine, EUserDeletionSource, EUserOnboardingStage, EUserRole } from 'shared/api';

// Roles that get a row tint. Roles absent from this map (User) render with no background.
export const ROLE_COLORS: Partial<Record<EUserRole, { bg: string; hoverBg: string }>> = {
  [EUserRole.Admin]: { bg: '#fff4e5', hoverBg: '#ffe9cc' },
  [EUserRole.Manager]: { bg: '#e5f6fd', hoverBg: '#cfeefb' },
};

// sx fragment for a table row, by role. Empty object for untinted roles.
export const getRoleRowSx = (role: EUserRole): SxProps<Theme> => {
  const c = ROLE_COLORS[role];
  if (!c) return {};
  return {
    backgroundColor: c.bg,
    '&.MuiTableRow-hover:hover': { backgroundColor: c.hoverBg },
  };
};

export const getUserRoleText = (role: EUserRole | undefined): string => {
  if (!role) return 'Не задана';

  if (role === EUserRole.Admin) return 'Administrator';
  if (role === EUserRole.Manager) return 'Manager';

  return 'User';
};

export const getTypeVaccination = (type: ETypeVaccine) => {
  if (type === ETypeVaccine.Vaccination) return 'V';

  return 'R';
};

// Возраст человека/питомца для карточки: «N years» от 2 лет, иначе «N mo»; невалидная/будущая дата → «—».
export const getSubjectAge = (birthday: string | null | undefined): string => {
  if (!birthday) return '—';
  const dob = new Date(birthday);
  if (Number.isNaN(dob.getTime())) return '—';
  const now = new Date();
  if (dob > now) return '—';
  const years = differenceInYears(now, dob);
  if (years >= 2) return `${years} years`;
  const months = differenceInMonths(now, dob);
  return `${months} mo`;
};

export const DELETION_SOURCE_META: Record<EUserDeletionSource, { label: string; color: 'default' | 'error' }> = {
  [EUserDeletionSource.SelfDelete]: { label: 'Self delete', color: 'default' },
  [EUserDeletionSource.AdminDelete]: { label: 'Admin delete', color: 'error' },
};

// Стадии воронки в её порядке: лейбл для чипа/фильтра, tooltip-описание и цвет чипа (серый → зелёный).
export const STAGE_META: Record<
  EUserOnboardingStage,
  { label: string; description: string; color: 'default' | 'warning' | 'info' | 'success' }
> = {
  [EUserOnboardingStage.CodeNotInserted]: {
    label: 'Code not inserted',
    description: 'Code was requested, but the person never entered the app',
    color: 'default',
  },
  [EUserOnboardingStage.NoOwnerProfile]: {
    label: 'No profile',
    description: 'Entered the app, but has not created any profile',
    color: 'warning',
  },
  [EUserOnboardingStage.NoVaccinations]: {
    label: 'No vaccinations',
    description: 'Has a profile, but no done vaccinations yet',
    color: 'info',
  },
  [EUserOnboardingStage.OnboardingCompleted]: {
    label: 'Completed',
    description: 'At least one vaccination on any people or pets',
    color: 'success',
  },
};
